import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import RichText, { detectDir } from "./RichText";
import LinkEmbed from "./LinkEmbed";
import { TactLoader } from "./Tactical";
import { toast } from "sonner";
import { Send, X } from "lucide-react";
import WhatsAppButton from "./WhatsAppButton";

/**
 * Slide-in right-side drawer that embeds a fully self-contained 1:1 DM
 * thread. Used from the Social page's "Friends" tab so clicking MSG opens
 * the chat in the same window instead of navigating to /chat.
 *
 * Props:
 *   peer — { id, username, game_id?, whatsapp?, avatar_url? }
 *   onClose — close callback
 *
 * Uses the same endpoints as /chat/:peerId (GET/POST /dm/:peerId/messages)
 * so history is shared across entry points.
 */
export default function DMChatDrawer({ peer, onClose }) {
  const { user } = useAuth();
  const { t } = useT();
  const peerId = peer?.id;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const sinceRef = useRef(null);

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const fetchMessages = useCallback(async (initial = false) => {
    if (!peerId) return;
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/dm/${peerId}/messages`, { params });
      if (data.length) {
        sinceRef.current = data[data.length - 1].created_at;
        setMessages((prev) => (initial ? data : [...prev, ...data]));
        setTimeout(scrollToBottom, 50);
      } else if (initial) {
        setMessages([]);
      }
    } catch (e) { /* noop */ }
    finally { if (initial) setLoading(false); }
  }, [peerId]);

  // Esc to close + poll messages every 5s.
  useEffect(() => {
    if (!peerId) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    sinceRef.current = null;
    setMessages([]);
    setLoading(true);
    fetchMessages(true);
    const iv = setInterval(() => fetchMessages(false), 5000);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearInterval(iv);
    };
  }, [peerId, fetchMessages, onClose]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (!peerId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [peerId]);

  if (!peer) return null;

  const onSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !peerId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/${peerId}/messages`, { text: text.trim() });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      setText("");
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="dm-drawer-root"
      className="fixed inset-0 z-[1000] flex justify-end bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Direct message with ${peer.username}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        data-testid="dm-drawer-panel"
        className="w-full sm:w-[440px] h-full bg-[#FFFFFF] dark:bg-[#1A1A1E] border-l border-[#E4E4E7] dark:border-[#2A2A30] shadow-[-10px_0_40px_rgba(0,0,0,0.5)] flex flex-col"
      >
        {/* Header */}
        <div className="border-b border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] p-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// direct message</div>
            <div className="font-heading text-xl uppercase tracking-wider truncate" data-testid="dm-drawer-peer-name">{peer.username}</div>
            {peer.game_id && <div className="font-mono text-[10px] text-[#71717A] truncate">{peer.game_id}</div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {peer.whatsapp && <WhatsAppButton number={peer.whatsapp} testId="dm-drawer-whatsapp" size="sm" />}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="dm-drawer-close"
              className="border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#E11D48] hover:border-[#E11D48] p-2 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="dm-drawer-list">
          {loading ? (
            <TactLoader label="LOADING MESSAGES" />
          ) : messages.length === 0 ? (
            <div className="font-mono text-[#71717A] text-sm text-center mt-10">[ NO MESSAGES YET — say hi. ]</div>
          ) : (
            messages.map((m) => {
              const mine = m.from_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-drawer-msg-${m.id}`}>
                  <div className={`max-w-[80%] px-3 py-2 border ${mine
                    ? "border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#09090B] dark:text-[#F4F4F5]"
                    : "border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#09090B] text-[#09090B] dark:text-[#F4F4F5]"}`}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words"><RichText text={m.text} /></div>
                    <div className="mt-1.5"><LinkEmbed text={m.text} /></div>
                    <div className="font-mono text-[9px] text-[#71717A] tracking-[0.2em] mt-1">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <form onSubmit={onSend} className="border-t border-[#E4E4E7] dark:border-[#2A2A30] p-3 flex gap-2" data-testid="dm-drawer-form">
          <input
            data-testid="dm-drawer-input"
            value={text}
            dir={detectDir(text) === "rtl" ? "rtl" : "auto"}
            onChange={(e) => setText(e.target.value)}
            className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-[#09090B] dark:text-[#F4F4F5] font-body focus:outline-none focus:ring-1 focus:ring-[#7C3AED] focus:border-[#7C3AED]"
            placeholder={t("dm.typeMsg") || "Type a message…"}
            maxLength={2000}
            autoFocus
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            data-testid="dm-drawer-send"
            className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-4 py-2 hover:bg-[#6D28D9] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Send className="w-3 h-3" />
          </button>
        </form>
      </div>
    </div>
  );
}
